// ---------------------------------------------------------------------------
// local-verify.ts — is this device-local file still there?
// ---------------------------------------------------------------------------
//
// A handle stored in IndexedDB points at a file on someone's disk. Between
// sessions that file can be moved, renamed, deleted, replaced, or sitting on a
// drive that is no longer plugged in — and the browser can additionally have
// dropped its read permission, which only a user gesture can restore.
//
// So a stored handle is a CLAIM, and playing without checking means the audio
// element fails with a media error that says nothing useful. This module turns
// the claim into one of four answers, ahead of playback.
//
// Every function takes the handle as a parameter rather than reaching for
// IndexedDB, which is what makes the whole decision table testable in Node with
// a stub handle — no browser, no filesystem, no mocking of a global.
// ---------------------------------------------------------------------------

import type { AudioFileHandle, LocalTrackRecord } from '@/core/lib/music-db';
import type { LocalTrackStatus } from './track';

export interface VerifyResult {
  status: LocalTrackStatus;
  /** The file, when it could be read. Callers turn this into an object URL. */
  file: File | null;
  /**
   * True when the bytes on disk differ from what was recorded at add time.
   * The file exists and is readable, but its duration and metadata are stale.
   */
  changed: boolean;
}

/**
 * Ask the browser whether we may read this handle, without touching the disk.
 *
 * Cheap enough to run across the whole library when the panel opens. A handle
 * from a browser that never had the permission API is treated as usable, since
 * the only way to find out there is to try reading it.
 */
export async function checkPermission(handle: AudioFileHandle): Promise<PermissionState> {
  if (typeof handle.queryPermission !== 'function') return 'granted';
  try {
    return await handle.queryPermission({ mode: 'read' });
  } catch {
    // An older or partial implementation. Assume readable and let the read decide.
    return 'granted';
  }
}

/**
 * Ask the user to restore read access. MUST be called from a user gesture.
 *
 * Outside a gesture the browser rejects it, which is why the UI shows an explicit
 * "Allow access" button rather than trying to do this on load.
 */
export async function requestPermission(handle: AudioFileHandle): Promise<PermissionState> {
  if (typeof handle.requestPermission !== 'function') return 'granted';
  try {
    return await handle.requestPermission({ mode: 'read' });
  } catch {
    return 'denied';
  }
}

/**
 * Does this error mean the file is gone, as opposed to something transient?
 *
 * `NotFoundError` is the spec's answer for a handle whose entry no longer
 * exists. `NotAllowedError` is a permission problem, which is recoverable with a
 * gesture and must NOT be reported as missing — telling someone their file was
 * deleted when it is merely locked would have them go looking for a backup.
 */
function isMissingError(err: unknown): boolean {
  const name = (err as { name?: string } | null)?.name;
  return name === 'NotFoundError';
}

function isPermissionError(err: unknown): boolean {
  const name = (err as { name?: string } | null)?.name;
  return name === 'NotAllowedError' || name === 'SecurityError';
}

/**
 * The full check, run immediately before playing a local track.
 *
 * This is the step that stops a moved file from becoming an unexplained media
 * error. It reads the file — the only way to know it is really there — so it is
 * not for sweeping the whole library on every render.
 */
export async function verifyLocalTrack(record: LocalTrackRecord): Promise<VerifyResult> {
  const permission = await checkPermission(record.handle);

  if (permission === 'denied') {
    return { status: 'needs-permission', file: null, changed: false };
  }
  if (permission === 'prompt') {
    // Recoverable, but only from a gesture. Reported rather than prompted for,
    // so a background schedule tick cannot trigger a permission dialog.
    return { status: 'needs-permission', file: null, changed: false };
  }

  try {
    const file = await record.handle.getFile();
    const changed = file.size !== record.sizeBytes || file.lastModified !== record.lastModified;
    return { status: 'ok', file, changed };
  } catch (err) {
    if (isPermissionError(err)) {
      return { status: 'needs-permission', file: null, changed: false };
    }
    if (isMissingError(err)) {
      return { status: 'missing', file: null, changed: false };
    }
    // Anything else — a disconnected drive, an I/O error — is reported as
    // missing too, because the practical answer for the player is the same: it
    // cannot play, so skip it. The distinction would not change what we do.
    return { status: 'missing', file: null, changed: false };
  }
}

/**
 * The cheap sweep, for when the library panel opens.
 *
 * Permission only: no disk read, so it stays fast over a long list. It cannot
 * detect a deleted file — `verifyLocalTrack` does that at play time — so `'ok'`
 * here means "nothing is obviously wrong", which is why `isPlayable` in track.ts
 * also treats `'unknown'` as worth attempting.
 */
export async function sweepPermissions(
  records: readonly LocalTrackRecord[],
): Promise<Map<string, LocalTrackStatus>> {
  const out = new Map<string, LocalTrackStatus>();
  const results = await Promise.all(
    records.map(async (r) => [r.id, await checkPermission(r.handle)] as const),
  );
  for (const [id, permission] of results) {
    out.set(id, permission === 'granted' ? 'ok' : 'needs-permission');
  }
  return out;
}

/** Strip a file extension for a default display title. */
export function titleFromFilename(name: string): string {
  const withoutPath = name.split(/[/\\]/).pop() ?? name;
  const dot = withoutPath.lastIndexOf('.');
  const base = dot > 0 ? withoutPath.slice(0, dot) : withoutPath;
  return base.trim() || withoutPath;
}
